import * as React from "react";
import { useState } from "react";
import styled from "styled-components";
import { PermissionGroups, PublicAmorgUser, rng, userHelper, UserToken } from '../../amorg-common';
import { getAuthorizedActiveUserOrRedirect, hasUserPermissions } from '../../api/serverApiHelpers';
import { getUsers } from "../../api/userClientApi";
import { PageHeader } from "../../designSystem/PageHeader";
import { Table } from '../../designSystem/Table';
import { LinkButton } from "../../designSystem/button/LinkButton";
import { colors } from "../../designSystem/colors/color";
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { SelectField } from "../../designSystem/formField/SelectField";
import { TextField } from "../../designSystem/formField/text/TextField";
import { OnOff } from "../../designSystem/inputs/OnOff";
import { SingleColumnLayout } from '../_layouts/SingleColumnLayout';
import { P } from '../../designSystem/typography/Body';
import { txt } from "../../txt";
import { clientAccessHelper } from '../../utils/clientAccessHelper';
import { ProtectedPageContext } from '../_contexts/ProtectedPageContext';
import { MemberPageWrapper } from '../_pageWrappers/MemberPageWrapper';
import CreateUserModalButton from "./_components/CreateUserModalButton";

export default function UserListingPage(): JSX.Element {
    return (
        <MemberPageWrapper selectedMenuItemKey='users' breadCrumbs={[{ text: 'Brugere', url: '' }]}>
            <PageHeader 
                narrow
                headerIcon='interface_user_users'
                headerText="Brugere" 
                headerStyle="secondary-gray" />
            <SingleColumnLayout narrow>
                <UserListingPageContent />
            </SingleColumnLayout>
        </MemberPageWrapper>
    );
}

function UserListingPageContent(): JSX.Element {
	const [activeUser, setActiveUser] = useState<UserToken | null>(null);
	const [users, setUsers] = useState<PublicAmorgUser[]>([]);
    const [searchText, setSearchText] = useState('');
    const [selectedRole, setSelectedRole] = useState('');
    const [selectedPermissionGroup, setSelectedPermissionGroup] = useState('');
    const [showDisabledUsers, setShowDisabledUsers] = useState(false);

	React.useEffect(() => {
		const getData = async () => {
			const result = await getAuthorizedActiveUserOrRedirect(['ViewUsers']);
			setActiveUser(result);
            await updateUsers();
		}
		getData();
	},[]);

    async function updateUsers()
    {
        setUsers((await getUsers(true, true)).sort((lhs,rhs) =>
            userHelper.getPublicUserFullName(lhs).localeCompare(userHelper.getPublicUserFullName(rhs))
    ));
    }

	async function handleUserCreated() {
		await updateUsers();
	}

	const isAdmin = activeUser && hasUserPermissions(activeUser, ['UserAdmin']);
	const includeSuperAdmins = activeUser !== null && activeUser.permissionGroupId === 'superadmin';
	const filteredUsers = users
        .filter(u => showDisabledUsers ? true : u.enabled)
        .filter(u => includeSuperAdmins || u.permissionGroup.id !== 'superadmin')
        .filter(u => searchText==='' 
            ? true 
            : (
                userHelper.getPublicUserFullName(u).toLowerCase().includes(searchText.toLowerCase()) ||
                u.email.toLowerCase().includes(searchText.toLowerCase())
            ))
        .filter(u => selectedRole==='' || u.currentRole?.title === selectedRole)
        .filter(u => selectedPermissionGroup==='' || u.permissionGroup.id === selectedPermissionGroup);

    const protectedUser = React.useContext(ProtectedPageContext).user;
    const hasAccess = clientAccessHelper.hasAllPermissions(protectedUser, ['ViewUsers']);

	return (
		    <>
          {!hasAccess
              ? <P>Hov! Du har ikke rettigheder til at tilgå denne side.</P>
              :
              <>
              <Flex justifyContent="flex-end">
                  {activeUser && isAdmin &&
                      <CreateUserModalButton onUserCreated={handleUserCreated} />
                  }
              </Flex>

              <Flex alignItems="flex-start" justifyContent="space-between">
                  
                  <TextField 
                      value={searchText}
                      onChange={(value) => setSearchText(value)}
                      label="Søg"
                      trailingIcon="interface_actions_search"
                      validator={() => ({valid:true}) }
                      placeholder="Søg på navn og email" />

                  <Flex flexDirection="column" gap={8}>

                      <Flex gap={8}>
                          <SelectField 
                              selectedValue={selectedRole} 
                              options={
                                  [
                                      'Alle',
                                      ...rng.getUniqueStrings(users.map(u => u.currentRole?.title || ''), { ignoreEmpty:true })
                                  ]
                                  .map(t => ({text:t, value:t}))
                              } 
                              onChange={(newValue) => newValue === 'Alle' ? setSelectedRole('') :  setSelectedRole(newValue)} 
                              label="Filtrér rolle" />
                          <SelectField 
                              selectedValue={selectedPermissionGroup} 
                              options={
                                  [
                                      {text:'Alle', value:''},
                                      { text:txt.PermissionGroupId_user, value:PermissionGroups.user.id},
                                      { text:txt.PermissionGroupId_member, value:PermissionGroups.member.id},
                                      { text:txt.PermissionGroupId_siteadmin, value:PermissionGroups.siteAdmin.id}
                                  ]
                              } 
                              onChange={(newValue) => setSelectedPermissionGroup(newValue)} 
                              label="Filtrér rettighedsniveau" />
                      </Flex>
                  
                      <Flex justifyContent="flex-end" gap={8}>
                          <span>Vis deaktiverede brugere</span>
                          <OnOff text={{
                              width: 24,
                              active:'',
                              inactive:'',
                          }} active={showDisabledUsers} onClick={(active)=>{ setShowDisabledUsers(active) }} />
                      
                      </Flex>
                  </Flex>

              </Flex>
              

              <div style={{padding:'10px 16px'}}>	   
                  <Table>
                      <thead>
                          <tr>
                              <th>Navn | Email</th>
                              <th>Rolle</th>
                              <th>Rettighedsniveau</th>
                              <th></th>
                          </tr>
                      </thead>
                      <tbody>
                          {filteredUsers.length > 0 &&
                              filteredUsers.map(u =>
                                  <tr key={u.id}>
                                      <td>{
                                          <>
                                              <Flex gap={4}>
                                                  <PrimaryCellText disabled={!u.enabled} lineThrough={!u.enabled}>
                                                      {userHelper.getPublicUserFullName(u)}
                                                  </PrimaryCellText>
                                                  {!u.enabled && 
                                                      <PrimaryCellText disabled={!u.enabled}>
                                                          (deaktiveret)
                                                      </PrimaryCellText>
                                                  }
                                              </Flex>
                                              <SecondaryCellText>
                                                  {u.email}
                                              </SecondaryCellText>
                                          </>
                                          }
                                      </td>
                                      <td style={{width:'300px'}}>
                                          <PrimaryCellText disabled={!u.enabled}>
                                              {u.currentRole?.title || ''}
                                          </PrimaryCellText>
                                      </td>
                                      <td style={{width:'200px'}}>
                                          <PrimaryCellText disabled={!u.enabled}>
                                              {u.permissionGroup.name}
                                          </PrimaryCellText>
                                      </td>
                                      <td style={{textAlign:'right', width:'50px'}}>
                                          {activeUser && isAdmin && 
                                              <LinkButton variant="text" icon="arrows_chevron_chevronForward"
                                                  href={`/users/${u.id}`} />
                                          }
                                      </td>
                                  </tr>
                              )
                          }
                      </tbody>
                  </Table>
              </div>
              </>
          }
      </>
	);
}

const PrimaryCellText = styled.span<{disabled?:boolean, lineThrough?:boolean}>`
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0.5px;
    text-decoration: ${props => props.lineThrough ? 'line-through' : 'none'};
    color:${props => props.disabled ? props.theme.colors.text.inactive : colors.text.normal};
`;
const SecondaryCellText = styled.div`
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.25px;
    color:${colors.text.normal};
`;